import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import './runHealth.css';

// Run health (STRY0038849): the one place the portal turns the vendor health
// check's public view into words. Source Health's Run requirements view and
// every vendor's History tab both read these maps, so a run or an area reads the
// same everywhere.
//
// Customer-facing rule: show only what the API returns (area labels, outcomes,
// messages, times). The API never sends probe names, check counts, error codes,
// sample ids or field names, and nothing here adds any.

// The six requirement areas, in display order. Labels head the Source Health
// grid's columns; a row always shows the label the API sent with the area.
export const AREAS = [
  { key: 'access', label: 'Access' },
  { key: 'productLookup', label: 'Product lookup' },
  { key: 'bugSearch', label: 'Bug search' },
  { key: 'dataFields', label: 'Data fields' },
  { key: 'sourceFreshness', label: 'Source freshness' },
  { key: 'extraDetails', label: 'Extra details' }
];

// A run's status. History reads "Passed" where Source Health reads "Operational".
// shortLabel is the Source Health vendor list's, where the pill shares a line with
// the vendor name; none has no short label (the line below says "No checks yet").
export const RUN_STATUSES = {
  operational: { label: 'Operational', historyLabel: 'Passed', shortLabel: 'OK', tone: 'ok' },
  degraded: { label: 'Degraded', shortLabel: 'Degraded', tone: 'warn' },
  stopped: { label: 'Stopped at health check', shortLabel: 'Stopped', tone: 'bad' },
  retrying: { label: 'Retrying', shortLabel: 'Retrying', tone: 'info' },
  none: { label: 'Not checked yet', tone: null }
};

// An area's outcome. "empty" passed: access works and there was nothing to check.
export const OUTCOMES = {
  pass: { label: 'Pass', tone: 'ok' },
  empty: { label: 'Pass', tone: 'ok', hollow: true, title: 'Access works, nothing to check right now' },
  degraded: { label: 'Degraded', tone: 'warn' },
  retry: { label: 'Retrying', tone: 'info' },
  fail: { label: 'Failed', tone: 'bad' }
};

export const NOT_USED_TITLE = 'Not used by this vendor';

// What each run status means, said once for the stat strip and the history callouts.
export const STOPPED_CALLOUT =
  'The run stopped before saving anything, so the next good run picks up every bug since the last good run.';
export const DEGRADED_CALLOUT =
  'Everything required passed, so bugs were saved. Some extra details are missing for this run.';

// An unknown status reads as not checked rather than as a code.
export const runStatusKey = status => (Object.prototype.hasOwnProperty.call(RUN_STATUSES, status) ? status : 'none');

export const runStatusLabel = (status, { history = false } = {}) => {
  const entry = RUN_STATUSES[runStatusKey(status)];
  return (history && entry.historyLabel) || entry.label;
};

export const outcomeOf = outcome =>
  Object.prototype.hasOwnProperty.call(OUTCOMES, outcome) ? OUTCOMES[outcome] : null;

const AREA_ORDER = new Map(AREAS.map((a, i) => [a.key, i]));
const areaIndex = area => (AREA_ORDER.has(area?.area) ? AREA_ORDER.get(area.area) : AREAS.length);

// The areas a vendor checks, in display order.
export const sortAreas = areas =>
  (Array.isArray(areas) ? areas.filter(a => a && typeof a === 'object') : [])
    .map((a, i) => [a, i])
    .sort(([a, i], [b, j]) => areaIndex(a) - areaIndex(b) || i - j)
    .map(([a]) => a);

export const areaByKey = areas => new Map(sortAreas(areas).map(a => [a.area, a]));

// The area that most needs attention: fail, then retry, then degraded, the first
// in display order on a tie. Null when every area passed.
const ATTENTION_RANK = { fail: 3, retry: 2, degraded: 1 };
export const worstArea = areas =>
  sortAreas(areas).reduce((worst, a) => {
    const rank = ATTENTION_RANK[a.outcome] || 0;
    if (!rank) return worst;
    return !worst || rank > ATTENTION_RANK[worst.outcome] ? a : worst;
  }, null);

// "Oct 28, 06:12" in the viewer's local time.
export const formatRunTime = iso => {
  if (!iso) return null;
  const m = moment(iso);
  return m.isValid() ? m.format('MMM D, HH:mm') : null;
};

// The Checked column: the local time, with the date when it isn't the run's own
// day, and " · reused" when the result was carried over from an earlier run.
export const formatChecked = (checkedAt, reused, runIso) => {
  const m = checkedAt ? moment(checkedAt) : null;
  if (!m || !m.isValid()) return '—';
  const run = runIso ? moment(runIso) : null;
  const sameDay = run && run.isValid() && run.isSame(m, 'day');
  const time = m.format(sameDay ? 'HH:mm' : 'MMM D, HH:mm');
  return reused ? `${time} · reused` : time;
};

// short: the vendor list's pill (shortLabel, full label on hover); nothing for a
// status without one.
export const RunPill = ({ status, history, short }) => {
  const entry = RUN_STATUSES[runStatusKey(status)];
  const label = runStatusLabel(status, { history });
  if (short && !entry.shortLabel) return null;
  return (
    <span className={`bz-pill${entry.tone ? ` bz-pill--${entry.tone}` : ''}`} title={short ? label : undefined}>
      <span className="bz-dot" aria-hidden="true" />
      {short ? entry.shortLabel : label}
    </span>
  );
};
RunPill.propTypes = { status: PropTypes.string, history: PropTypes.bool, short: PropTypes.bool };

export const NotUsed = ({ title = NOT_USED_TITLE }) => (
  <span className="rh-na" title={title}>
    <span aria-hidden="true">—</span>
    <span className="rh-sr">{title}</span>
  </span>
);
NotUsed.propTypes = { title: PropTypes.string };

// compact: the denser chip the Source Health grid uses in its area columns.
export const OutcomePill = ({ outcome, compact }) => {
  const entry = outcomeOf(outcome);
  if (!entry) return <NotUsed title="No result" />;
  return (
    <span
      className={`bz-pill bz-pill--${entry.tone}${entry.hollow ? ' rh-pill--hollow' : ''}${
        compact ? ' rh-pill--compact' : ''
      }`}
      title={entry.title || entry.label}
    >
      <span className="bz-dot" aria-hidden="true" />
      {entry.label}
      {entry.hollow && <span className="rh-sr">: {entry.title.toLowerCase()}</span>}
    </span>
  );
};
OutcomePill.propTypes = { outcome: PropTypes.string, compact: PropTypes.bool };

export const areaShape = PropTypes.shape({
  area: PropTypes.string,
  label: PropTypes.string,
  outcome: PropTypes.string,
  message: PropTypes.string,
  checkedAt: PropTypes.string,
  reused: PropTypes.bool
});

// The areas one run checked: Requirement | Outcome | What it means | Checked
// (History puts Outcome first, as its mock does).
export const AreasTable = ({ areas, runIso, label, outcomeFirst }) => {
  const rows = sortAreas(areas);
  const head = outcomeFirst
    ? ['Outcome', 'Requirement', 'What it means', 'Checked']
    : ['Requirement', 'Outcome', 'What it means', 'Checked'];
  return (
    <table className={`bz-table rh-areas${outcomeFirst ? ' rh-areas--outcome-first' : ''}`} aria-label={label}>
      <colgroup>
        <col className="rh-areas__c1" />
        <col className="rh-areas__c2" />
        <col />
        <col className="rh-areas__c4" />
      </colgroup>
      <thead>
        <tr>
          {head.map(h => (
            <th key={h} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((a, i) => {
          const requirement = (
            <td key="req" className="rh-areas__req">
              {a.label}
            </td>
          );
          const outcome = (
            <td key="out">
              <OutcomePill outcome={a.outcome} />
            </td>
          );
          return (
            <tr key={a.area || i}>
              {outcomeFirst ? [outcome, requirement] : [requirement, outcome]}
              <td className="rh-areas__msg">{a.message}</td>
              <td className="bz-mono rh-areas__when">{formatChecked(a.checkedAt, a.reused, runIso)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
AreasTable.propTypes = {
  areas: PropTypes.arrayOf(areaShape),
  runIso: PropTypes.string,
  label: PropTypes.string.isRequired,
  outcomeFirst: PropTypes.bool
};
